import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";

export type UploadProgressUpdate = {
  fileOperationId: string;
  progress: number;
  status?: string;
};

type Listener = (data: UploadProgressUpdate) => void;

class UploadProgressHub {
  private connection: HubConnection | null = null;
  private listeners = new Set<Listener>();
  private joinedGroups = new Set<string>();
  private startingPromise: Promise<void> | null = null;

  private emit = (payload: UploadProgressUpdate) => {
    this.listeners.forEach((listener) => listener(payload));
  };

  public subscribe(listener: Listener, ids?: Set<string>): () => void {
    const wrapped: Listener = ids
      ? (data) => { if (ids.has(data.fileOperationId)) listener(data); }
      : listener;
    this.listeners.add(wrapped);
    return () => this.listeners.delete(wrapped);
  }

  public async connect(baseUrl: string, accessToken: string): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) return;
    if (this.startingPromise) return this.startingPromise;

    this.startingPromise = (async () => {
      const connection = new HubConnectionBuilder()
        .withUrl(`${baseUrl}/hubs/upload-progress`, {
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect()
        .build();

      const handler = (data: any) => {
        const fileOperationId = data?.fileOperationId ?? data?.FileOperationId;
        if (!fileOperationId) return;
        this.emit({
          fileOperationId,
          progress: Number(data.progress ?? data.Progress ?? data.percent ?? data.Percent ?? 0),
          status: data.status ?? data.Status ?? data.stage ?? data.Stage,
        });
      };

      connection.on("UploadProgressUpdated", handler);

      // Log connection lifecycle events to diagnose reconnection issues.
      // withAutomaticReconnect() already retries; these just surface what happens.
      connection.onreconnecting((err) =>
        console.warn("[UploadProgressHub] Connection lost, reconnecting:", err)
      );
      connection.onreconnected((connectionId) =>
        console.info("[UploadProgressHub] Reconnected:", connectionId)
      );
      connection.onclose((err) => {
        if (err) console.error("[UploadProgressHub] Connection closed with error:", err);
        this.connection = null;
        this.startingPromise = null;
      });

      await connection.start();
      this.connection = connection;
    })();

    try {
      await this.startingPromise;
    } finally {
      this.startingPromise = null;
    }
  }

  public async leaveGroup(fileOperationId: string): Promise<void> {
    if (!this.connection || !this.joinedGroups.has(fileOperationId)) return;
    // Hub verb is Unsubscribe. Swallow errors so a failed leave (e.g. a connection
    // already dropping) does not break group cleanup.
    try { await this.connection.invoke("Unsubscribe", fileOperationId); } catch {}
    this.joinedGroups.delete(fileOperationId);
  }

  public async leaveGroups(fileOperationIds: string[]): Promise<void> {
    await Promise.all(fileOperationIds.filter(Boolean).map((id) => this.leaveGroup(id)));
  }

  public async stop(): Promise<void> {
    if (this.joinedGroups.size > 0 || !this.connection) return;
    await this.connection.stop();
    this.connection = null;
  }

  public async joinGroup(fileOperationId: string): Promise<void> {
    if (!this.connection || this.joinedGroups.has(fileOperationId)) return;
    // Hub verb is Subscribe. Let a failed subscribe surface — the caller treats it as
    // "cannot track this upload" (see UploadNotificationContext).
    await this.connection.invoke("Subscribe", fileOperationId);
    this.joinedGroups.add(fileOperationId);
  }

  public async joinGroups(fileOperationIds: string[]): Promise<void> {
    await Promise.all(fileOperationIds.filter(Boolean).map((id) => this.joinGroup(id)));
  }
}

export const uploadProgressHub = new UploadProgressHub();
